import dayjs from "dayjs";
import { formatAmount } from "../../../utils";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";
import {
  INSURANCE_DUE_DATE_REPORT,
  INSURANCE_SUMMARY_REPORT,
} from "./insuranceReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const INSURANCE_TYPE_LABEL_BY_API: Record<string, string> = {
  TERM: "Term",
  ENDOWMENT: "Endowment",
  ANNUITY: "Annuity",
  LIC: "LIC policies",
  LIC_POLICIES: "LIC policies",
  LIFE: "Life",
  HEALTH: "Health",
  ULIP: "Ulip",
  NPS: "NPS",
  OTHERS: "Others",
  OTHER: "Others",
};

const isFormValueEmpty = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

export const mapInsuranceTypeToLabel = (val: unknown): string => {
  if (!val) return "";
  const raw = String(val).trim();
  const upper = raw.toUpperCase().replace(/\s+/g, "_");
  if (INSURANCE_TYPE_LABEL_BY_API[upper]) {
    return INSURANCE_TYPE_LABEL_BY_API[upper];
  }
  return raw;
};

export const getInsuranceInsuredName = (
  item: Record<string, any> | null | undefined,
): string => {
  if (!item) return "-";
  const txns = Array.isArray(item.transactions) ? item.transactions : [];
  for (const txn of txns) {
    const txnSource = { ...txn, ...(txn.metadata || {}) };
    const name = txnSource.insuredName;
    if (!isFormValueEmpty(name)) {
      return String(name).trim();
    }
  }
  if (!isFormValueEmpty(item.insuredName)) {
    return String(item.insuredName).trim();
  }
  return "-";
};

const parseMfTransactionDate = (value: unknown): number => {
  if (value == null || value === "" || value === "-") return 0;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return n;
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split("/");
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const pickInsuranceReportField = (
  item: Record<string, any>,
  key: string,
): unknown => {
  const hasValue = (val: unknown): val is string | number | boolean =>
    !isFormValueEmpty(val);

  if (hasValue(item?.[key])) return item[key];

  const meta = item?.metadata ?? item?.meta ?? {};
  if (hasValue(meta[key])) return meta[key];

  if (item?.txnType || item?.purchaseDate || item?.metadata) {
    return null;
  }

  const txns = Array.isArray(item?.transactions) ? item.transactions : [];
  for (const txn of txns) {
    if (hasValue(txn?.[key])) return txn[key];
    const txnMeta = txn?.metadata ?? txn?.meta ?? {};
    if (hasValue(txnMeta[key])) return txnMeta[key];
  }

  return null;
};

const isValidInsuranceReportDateMs = (ms: number): boolean => {
  if (!ms || Number.isNaN(ms)) return false;
  const year = new Date(ms).getFullYear();
  return year >= 1980 && year <= 2100;
};

const formatInsuranceReportDate = (value: unknown): string => {
  if (isFormValueEmpty(value)) return "-";
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const ms = parseMfTransactionDate(raw);
    return isValidInsuranceReportDateMs(ms) ? raw : "-";
  }
  const ms = parseMfTransactionDate(value);
  if (!isValidInsuranceReportDateMs(ms)) return "-";
  return dayjs(ms).format("DD/MM/YYYY");
};

const formatInsuranceReportAmount = (value: unknown): string => {
  if (isFormValueEmpty(value)) return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `₹${formatAmount(num)}`;
};

const normalizeInsuranceReportRow = (row: Record<string, any>): Record<string, any> => {
  const insuranceTypeRaw =
    row.insuranceType ??
    pickInsuranceReportField(row, "insuranceType") ??
    row.type;
  const policyName =
    row.policyName ??
    pickInsuranceReportField(row, "policyName") ??
    row.insurerName ??
    row.schemeName ??
    row.name;
  const policyNumber =
    row.policyNumber ?? pickInsuranceReportField(row, "policyNumber") ?? row.policyNo;
  const insuredNameRaw =
    pickInsuranceReportField(row, "insuredName") ??
    (getInsuranceInsuredName(row) !== "-" ? getInsuranceInsuredName(row) : null);
  const sumAssured = row.sumAssured ?? pickInsuranceReportField(row, "sumAssured");
  const firstPremium =
    pickInsuranceReportField(row, "firstPremium") ??
    row.firstPremium ??
    row.amount ??
    row.investedAmount;
  const nextPremiumDue = pickInsuranceReportField(row, "nextPremiumDue");
  const nextPremiumAmount = pickInsuranceReportField(row, "nextPremiumAmount");
  const maturityDate = row.maturityDate ?? pickInsuranceReportField(row, "maturityDate");
  const purchaseDate = row.purchaseDate ?? pickInsuranceReportField(row, "purchaseDate");
  const nominee = pickInsuranceReportField(row, "nominee");

  const txnCostMeta: Record<string, unknown> = {};
  for (const key of ["stt", "brokerage", "stampDuty", "otherExpenses", "dpName"] as const) {
    const val = pickInsuranceReportField(row, key);
    if (val !== null && val !== undefined) txnCostMeta[key] = val;
  }

  return withTxnMetadataFields({
    ...row,
    metadata: { ...(row.metadata ?? row.meta ?? {}), ...txnCostMeta },
    insuranceType: mapInsuranceTypeToLabel(insuranceTypeRaw) || "-",
    policyName: policyName || "-",
    policyNumber: policyNumber || "-",
    insuredName: isFormValueEmpty(insuredNameRaw) ? "-" : String(insuredNameRaw).trim(),
    sumAssured: formatInsuranceReportAmount(sumAssured),
    firstPremium: formatInsuranceReportAmount(firstPremium),
    nextPremiumDue: formatInsuranceReportDate(nextPremiumDue),
    nextPremiumAmount: formatInsuranceReportAmount(nextPremiumAmount),
    maturityDate: formatInsuranceReportDate(maturityDate),
    purchaseDate: formatInsuranceReportDate(purchaseDate),
    nominee: isFormValueEmpty(nominee) ? "-" : String(nominee).trim(),
  });
};

const extractInsuranceHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const info =
    payload?.totalHoldingInfo ??
    payload?.totalHolding ??
    payload?.data?.totalHoldingInfo ??
    null;
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return info;
};

export type ParsedInsuranceReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parseInsuranceReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedInsuranceReport => {
  const holdings = extractInsuranceHoldings(payload);
  const rows = holdings.map(normalizeInsuranceReportRow);

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo: extractTotalHoldingInfo(payload),
    totalPages: Number(payload?.totalPages) || 1,
    pageIndex: Number(payload?.pageIndex) || 1,
    totalRecords: Number(payload?.totalRecords) ?? rows.length,
  };
};

export { INSURANCE_DUE_DATE_REPORT, INSURANCE_SUMMARY_REPORT };
